import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Navbar } from './components/Navbar';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { GoogleCallback } from './pages/GoogleCallback';
import { Dashboard } from './pages/Dashboard';
import { UserDashboard } from './pages/UserDashboard';
import { Users } from './pages/Users';
import { Logs } from './pages/Logs';
import { Analytics } from './pages/Analytics';
import { ApiTester } from './pages/ApiTester';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [adminSecret, setAdminSecret] = useState(null);
  
  useEffect(() => {
    // Check if user is logged in
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedAdminSecret = localStorage.getItem('adminSecret');
    
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    if (storedAdminSecret) {
      setAdminSecret(storedAdminSecret);
    }
  }, []);
  
  const handleLogin = useCallback((accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
  }, []);
  
  const handleAdminLogin = useCallback((secret) => {
    setAdminSecret(secret);
    localStorage.setItem('adminSecret', secret);
  }, []);
  
  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    setAdminSecret(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminSecret');
  }, []);
  
  const ProtectedRoute = ({ children }) => {
    if (!token) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };
  
  const AdminProtectedRoute = ({ children }) => {
    if (!adminSecret) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };
  
  return (
    <div className="App min-h-screen bg-gray-50">
      <BrowserRouter>
        <Navbar isLoggedIn={!!user} isAdmin={!!adminSecret} onLogout={handleLogout} user={user} />
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login onLogin={handleLogin} onAdminLogin={handleAdminLogin} />} />
            <Route path="/auth/callback" element={<GoogleCallback onLogin={handleLogin} />} />
            
            {/* User Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <UserDashboard user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-tester"
              element={
                <ProtectedRoute>
                  <ApiTester />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <AdminProtectedRoute>
                  <Dashboard adminSecret={adminSecret} />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <AdminProtectedRoute>
                  <Users adminSecret={adminSecret} />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <AdminProtectedRoute>
                  <Logs adminSecret={adminSecret} />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <AdminProtectedRoute>
                  <Analytics adminSecret={adminSecret} />
                </AdminProtectedRoute>
              }
            />
          </Routes>
        </main>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;